import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaQrcode, FaCopy } from 'react-icons/fa';

const Payment = () => {
  const [qrData, setQrData] = useState(null);
  const [utrNumber, setUtrNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQRCode();
  }, []);

  const fetchQRCode = async () => {
    try {
      const response = await paymentAPI.getQRCode();
      setQrData(response.data.qrCode);
    } catch (error) {
      console.error('Error fetching QR code:', error);
      toast.error('Failed to load payment QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUPI = () => {
    if (qrData?.upiId) {
      navigator.clipboard.writeText(qrData.upiId);
      toast.success('UPI ID copied to clipboard!');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!/^\d{12}$/.test(utrNumber)) {
      toast.error('UTR number must be 12 digits');
      return;
    }

    setSubmitting(true);

    try {
      await paymentAPI.submitPayment({ utrNumber });
      toast.success('Payment submitted successfully! Awaiting verification.');
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Payment submission error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-400 text-white p-8 text-center">
            <FaQrcode className="mx-auto mb-4" size={48} />
            <h1 className="text-3xl font-bold mb-2">Complete Your Payment</h1>
            <p className="text-primary-100">Scan QR code and submit UTR number</p>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* QR Code Section */}
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-secondary-900 mb-4">
                    Scan QR Code to Pay
                  </h2>
                  <div className="bg-white p-6 rounded-xl border-2 border-primary-200 inline-block">
                    {qrData?.image ? (
                      <img
                        src={qrData.image}
                        alt="Payment QR Code"
                        className="w-64 h-64 mx-auto"
                      />
                    ) : (
                      <div className="w-64 h-64 bg-secondary-100 flex items-center justify-center rounded">
                        <p className="text-secondary-500">QR Code not available</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-primary-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-secondary-900 mb-3">Payment Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Amount:</span>
                      <span className="font-bold text-primary-600 text-lg">
                        ₹{qrData?.amount || 5500}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-secondary-600">UPI ID:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{qrData?.upiId}</span>
                        <button
                          onClick={handleCopyUPI}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          <FaCopy />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Payee Name:</span>
                      <span className="font-medium">{qrData?.upiName}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* UTR Submission Section */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-secondary-900 mb-4">
                    Submit Payment Proof
                  </h2>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-yellow-800 font-medium">
                      Important: After making the payment, you will receive a UTR/Transaction ID.
                      Please enter it below.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label
                        htmlFor="utrNumber"
                        className="block text-sm font-medium text-secondary-700 mb-2"
                      >
                        UTR/Transaction Number *
                      </label>
                      <input
                        id="utrNumber"
                        type="text"
                        required
                        maxLength="12"
                        value={utrNumber}
                        onChange={(e) => setUtrNumber(e.target.value.replace(/\D/g, ''))}
                        className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        placeholder="Enter 12-digit UTR number"
                      />
                      <p className="text-xs text-secondary-500 mt-1">
                        The UTR number is a 12-digit unique transaction reference number
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || utrNumber.length !== 12}
                      className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          <FaCheckCircle />
                          Submit Payment
                        </>
                      )}
                    </button>
                  </form>
                </div>

                {/* Instructions */}
                <div className="bg-secondary-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-secondary-900 mb-3">Instructions</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-secondary-700">
                    <li>Open any UPI app (Google Pay, PhonePe, Paytm, etc.)</li>
                    <li>Scan the QR code or use the UPI ID</li>
                    <li>Pay exactly ₹{qrData?.amount || 5500}</li>
                    <li>Note down the UTR/Transaction ID</li>
                    <li>Enter the UTR number above and submit</li>
                    <li>Wait for admin verification (usually within 24 hours)</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
